import { P, Pivot, R, SharedColors, SharedGradients } from "apparatus/library/common";
import { Ray } from "apparatus/library/physics/optical/common";
import { Group, Path, Shape } from "paper";
const MIN_WIDTH = 20;
const MAX_WIDTH = 200;
const MIN_LENGTH = 80;
const MAX_LENGTH = 400;
export class GlassBlock {
    constructor() {
        this.drivers = [
            {
                type: "rectangle",
                minX: MIN_LENGTH / 2, maxX: MAX_LENGTH / 2,
                minY: MIN_WIDTH, maxY: MAX_WIDTH,
                toAppearance: (f, p) => f({ length: p.x * 2, width: p.y }),
                fromAppearance: a => P(a.length / 2, a.width),
                scale: 1.0,
            }
        ];
    }
    render(appearance) {
        const { ior, width, length, incidentLength, emergentLength, rayThickness } = appearance;
        let incidenceAngleD = appearance.incidenceAngleD;
        const glass = Shape.Rectangle(R(0, 0, length, width, Pivot.TOP_CENTER))
            .withGradientFill("right-down", SharedGradients.glass)
            .withStroke("thin", SharedColors.glassEdge);
        const graphic = new Group();
        if (appearance.showRay) {
            // Precalculate max incidenceAngle.
            const maxRefractionAngleD = Math.atan((length - 10) / width).toDegrees();
            const maxIncidenceAngleD = Math.asin(Math.sin(maxRefractionAngleD.toRadians()) * ior).toDegrees();
            if (maxIncidenceAngleD) {
                incidenceAngleD = Math.min(maxIncidenceAngleD, incidenceAngleD);
            }
            // IOR = sin(i) / sin(r)
            const refractionAngleD = Math.asin(Math.sin(incidenceAngleD.toRadians()) / ior).toDegrees();
            // Calculate the point of entry (top) and exit (top) such that the ray passes through
            // rectangle center.
            const entryPoint = P(-Math.tan(refractionAngleD.toRadians()) * width / 2, 0);
            const exitPoint = P(-entryPoint.x, width);
            const ray = new Path();
            ray.addSegments([
                entryPoint.add(P(0, -incidentLength).rotate(-incidenceAngleD)),
                entryPoint,
                exitPoint,
                exitPoint.add(P(0, emergentLength).rotate(-incidenceAngleD))
            ]);
            ray.withStroke(rayThickness, appearance.rayColor);
            graphic.insertChild(0, ray);
            Ray.maybeAddArrows(appearance, ray, [0.5, 0.5, 1.0], graphic);
        }
        graphic.addChild(glass);
        return {
            graphic,
            hitShape: glass.bounds.expand(5).toShape(),
            pivot: glass.bounds.center,
        };
    }
}
GlassBlock.properties = {
    label: "Glass Block",
    flippable: true,
    defaultAppearance: Object.assign(Object.assign({ incidenceAngleD: 40, ior: 1.517, length: 100, width: 40, incidentLength: 100, emergentLength: 100 }, Ray.defaultAppearanceRayOn), Ray.defaultAppearanceArrow),
    appearanceSpecs: [
        {
            spec: "slider", key: "width", label: "Width",
            min: MIN_WIDTH, max: MAX_WIDTH,
        },
        {
            spec: "slider", key: "length", label: "Length",
            min: MIN_LENGTH, max: MAX_LENGTH,
        },
        ...Ray.specs,
        {
            spec: "slider", key: "incidenceAngleD", label: "Angle of incidence",
            min: 0, max: 89, visible: a => a.showRay, unit: "°", pipsStep: 45
        },
        {
            spec: "slider", key: "ior", label: "Index of refraction",
            min: 1.0, max: 3.0, visible: a => a.showRay, step: 0.001, pipsStep: 1000,
        },
        {
            spec: "slider", key: "incidentLength", label: "Incident ray",
            min: 10, max: 300, visible: a => a.showRay
        },
        {
            spec: "slider", key: "emergentLength", label: "Emergent ray",
            min: 10, max: 300, visible: a => a.showRay
        },
        ...Ray.arrowSpecs,
    ],
};
